import { Bar, Doughnut } from 'react-chartjs-2'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend } from 'chart.js'
import type { Submission } from '../../lib/types'
import { count } from '../../lib/stats'
import { C } from '../../lib/colors'

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend)

const BASE = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }
const GRID_COLOR = 'rgba(1,87,98,0.07)'

interface Props { data: Submission[] }

export function CycleHealth({ data }: Props) {
  const classKeys   = ['regular', 'short', 'long', 'very-long', 'irregular', 'absent']
  const classLabels = ['Regular', 'Short', 'Long', 'Very long', 'Irregular', 'Absent']

  const clBins = [18, 22, 25, 28, 31, 34, 37, 43]
  const clLabels = clBins.slice(0, -1).map((v, i) => `${v}–${clBins[i + 1] - 1}`)
  const clColors = clBins.slice(0, -1).map((v, i) => (v >= 25 && clBins[i + 1] <= 36) ? C.teal : C.a(C.teal, .4))
  const clData = data.map(d => d.cycle_length_days).filter((v): v is number => v !== null)
  const clCounts = clBins.slice(0, -1).map((v, i) => count(clData, l => l >= v && l < clBins[i + 1]))

  const cvData = data.map(d => d.cycle_variation_days).filter((v): v is number => v !== null)
  const cvBins = [0, 2, 4, 6, 8, 11, 15]
  const cvLabels = cvBins.slice(0, -1).map((v, i) => `${v}–${cvBins[i + 1] - 1}d`)
  const cvCounts = cvBins.slice(0, -1).map((v, i) => count(cvData, l => l >= v && l < cvBins[i + 1]))

  const srcKeys   = ['user-entered', 'bracket-estimated', 'bracket-only']
  const srcLabels = ['User-entered', 'Bracket-estimated', 'Bracket-only']

  return (
    <section className="sec" id="cycle">
      <div className="sec-hd">
        <div className="sec-label">07 · Cycle Health Landscape</div>
        <h2 className="sec-title">Cycle classification, length, and variation</h2>
        <p className="sec-sub">cycle_classification is a stored derived field. cycle_length_days and cycle_variation_days are bracket-estimated or user-entered.</p>
      </div>
      <div className="g3" style={{ marginBottom: 12 }}>
        <div className="card">
          <div className="card-title">Cycle classification</div>
          <div className="card-sub">Derived from Q1 answer key + Q5 (absent override)</div>
          <div className="cw h220">
            <Doughnut
              data={{ labels: classLabels, datasets: [{ data: classKeys.map(k => count(data, d => d.cycle_classification === k)), backgroundColor: C.cycle, borderWidth: 0, hoverOffset: 6 }] }}
              options={{ ...BASE, plugins: { legend: { display: true, position: 'bottom' as const, labels: { boxWidth: 10, padding: 10, font: { size: 10 } } } } }}
            />
          </div>
        </div>
        <div className="card span2">
          <div className="card-title">Cycle length distribution</div>
          <div className="card-sub">Bracket-estimated (or user-entered) cycle length in days. Reference 25–35 highlighted.</div>
          <div className="cw h220">
            <Bar
              data={{ labels: clLabels, datasets: [{ data: clCounts, backgroundColor: clColors, borderRadius: 4 }] }}
              options={{ ...BASE, scales: { x: { grid: { display: false } }, y: { grid: { color: GRID_COLOR } } } }}
            />
          </div>
        </div>
      </div>
      <div className="g2">
        <div className="card">
          <div className="card-title">Cycle variation</div>
          <div className="card-sub">User-entered day-to-day variability (Q27 — optional). Null where skipped.</div>
          {cvData.length > 0 ? (
            <div className="cw h200">
              <Bar
                data={{ labels: cvLabels, datasets: [{ data: cvCounts, backgroundColor: cvCounts.map((_, i) => C.a(C.rose, 0.3 + i * 0.1)), borderRadius: 4 }] }}
                options={{ ...BASE, scales: { x: { grid: { display: false } }, y: { grid: { color: GRID_COLOR } } } }}
              />
            </div>
          ) : (
            <p className="card-sub">No variation data entered yet — Q27 is optional and no respondents have provided it.</p>
          )}
        </div>
        <div className="card">
          <div className="card-title">Data source provenance</div>
          <div className="card-sub">How cycle metrics were recorded: user-entered vs bracket-estimated vs bracket-only.</div>
          <div className="cw h200">
            <Doughnut
              data={{ labels: srcLabels, datasets: [{ data: srcKeys.map(k => count(data, d => d.cycle_data_source === k)), backgroundColor: [C.teal2, C.teal, C.a(C.teal, .35)], borderWidth: 0 }] }}
              options={{ ...BASE, plugins: { legend: { display: true, position: 'bottom' as const, labels: { boxWidth: 10, padding: 8, font: { size: 10 } } } } }}
            />
          </div>
        </div>
      </div>
    </section>
  )
}
